// ShareClassRowForm.tsx
import React, { useState, useEffect } from "react";
import {
  Control,
  Controller,
  UseFormSetValue,
  UseFormResetField,
  UseFormWatch,
  FieldErrors,
} from "react-hook-form";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormControlLabel,
  Checkbox,
  IconButton,
  Typography,
  Grid,
  Divider,
  Chip,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DeleteIcon from "@mui/icons-material/Delete";
import { FormValues, ExtractedShareClass } from "./types";
import {
  LLMBGColor,
  LLMBGColor__Select,
  LLMBGColor__Checkbox,
  STATUS_OPTIONS,
  CASH_AVAILABLE_DAYS_OPTIONS,
} from "./constants";

interface ShareClassRowFormProps {
  index: number;
  control: Control<FormValues>;
  setValue: UseFormSetValue<FormValues>;
  resetField: UseFormResetField<FormValues>;
  watch: UseFormWatch<FormValues>;
  errors: FieldErrors<FormValues>;
  onRemove: (index: number) => void;
  autoPopulatedFields: string[];
  setAutoPopulatedFields: React.Dispatch<React.SetStateAction<string[]>>;
  marketEnumId: number;
  feeCalcOptions: any[];
  feeFreqOptions: any[];
  peMgmtFeeCalcOptions: any[];
  peInctFeeCalcOptions: any[];
  calendarUnitOptions: any[];
  lockupEndValueOptions: any[];
  rightOfExtensionOptions: any[];
  compoundingPeriodOptions: any[];
  workoutOptions: any[];
  currencyOptions: any[];
}

const ShareClassRowForm: React.FC<ShareClassRowFormProps> = ({
  index,
  control,
  setValue,
  resetField,
  watch,
  errors,
  onRemove,
  autoPopulatedFields,
  setAutoPopulatedFields,
  marketEnumId,
  feeCalcOptions,
  feeFreqOptions,
  peMgmtFeeCalcOptions,
  peInctFeeCalcOptions,
  calendarUnitOptions,
  lockupEndValueOptions,
  rightOfExtensionOptions,
  compoundingPeriodOptions,
  workoutOptions,
  currencyOptions,
}) => {
  const [expanded, setExpanded] = useState(false);

  const shareClass = watch(`shareClasses.${index}`);
  const shareClassName = shareClass?.name || `Share Class ${index + 1}`;

  // Helper to check if field was auto-populated
  const isAutoPopulated = (fieldName: string) => {
    return autoPopulatedFields.includes(`shareClasses.${index}.${fieldName}`);
  };

  // Helper to get styling for auto-populated fields
  const getFieldStyle = (
    fieldName: string,
    fieldType: "text" | "select" | "checkbox" = "text"
  ) => {
    if (!isAutoPopulated(fieldName)) return {};

    switch (fieldType) {
      case "select":
        return LLMBGColor__Select;
      case "checkbox":
        return LLMBGColor__Checkbox;
      default:
        return LLMBGColor;
    }
  };

  // Handle field change to remove from auto-populated list
  const handleFieldChange = (fieldName: string) => {
    const fullPath = `shareClasses.${index}.${fieldName}`;
    setAutoPopulatedFields(prev => prev.filter(f => f !== fullPath));
  };

  return (
    <Accordion
      expanded={expanded}
      onChange={() => setExpanded(!expanded)}
      sx={{ mb: 2 }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="h6">{shareClassName}</Typography>
            {shareClass?.isExisting ? (
              <Chip
                label="Update"
                size="small"
                color="info"
                variant="outlined"
              />
            ) : (
              <Chip
                label="New"
                size="small"
                color="success"
                variant="outlined"
              />
            )}
          </Box>
          <IconButton
            size="small"
            color="error"
            onClick={e => {
              e.stopPropagation();
              onRemove(index);
            }}
            sx={{ mr: 1 }}
          >
            <DeleteIcon />
          </IconButton>
        </Box>
      </AccordionSummary>

      <AccordionDetails>
        <Grid container spacing={2}>
          {/* Basic Info Section */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Basic Information
            </Typography>
            <Divider sx={{ mb: 2 }} />
          </Grid>

          <Grid item xs={12} md={6}>
            <Controller
              name={`shareClasses.${index}.name`}
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Share Class Name"
                  fullWidth
                  required
                  onChange={e => {
                    field.onChange(e);
                    handleFieldChange("name");
                  }}
                  sx={getFieldStyle("name", "text")}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Controller
              name={`shareClasses.${index}.status`}
              control={control}
              render={({ field }) => (
                <FormControl fullWidth required>
                  <InputLabel>Status</InputLabel>
                  <Select
                    {...field}
                    label="Status"
                    onChange={e => {
                      field.onChange(e);
                      handleFieldChange("status");
                    }}
                    sx={getFieldStyle("status", "select")}
                  >
                    {STATUS_OPTIONS.map(opt => (
                      <MenuItem key={opt.id} value={opt.name}>
                        {opt.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Controller
              name={`shareClasses.${index}.isRcgNegotiated`}
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      {...field}
                      checked={field.value || false}
                      onChange={e => {
                        field.onChange(e);
                        handleFieldChange("isRcgNegotiated");
                      }}
                      sx={getFieldStyle("isRcgNegotiated", "checkbox")}
                    />
                  }
                  label="Is Negotiated"
                />
              )}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Controller
              name={`shareClasses.${index}.restricted`}
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      {...field}
                      checked={field.value || false}
                      onChange={e => {
                        field.onChange(e);
                        handleFieldChange("restricted");
                      }}
                      sx={getFieldStyle("restricted", "checkbox")}
                    />
                  }
                  label="Restricted"
                />
              )}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Controller
              name={`shareClasses.${index}.currencyDenominationEnumId`}
              control={control}
              render={({ field }) => (
                <FormControl fullWidth>
                  <InputLabel>Currency</InputLabel>
                  <Select
                    {...field}
                    label="Currency"
                    onChange={e => {
                      field.onChange(e);
                      handleFieldChange("currencyDenominationEnumId");
                    }}
                    sx={getFieldStyle("currencyDenominationEnumId", "select")}
                  >
                    {currencyOptions.map(opt => (
                      <MenuItem key={opt.id} value={opt.id}>
                        {opt.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Controller
              name={`shareClasses.${index}.otherFees`}
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Other Fees"
                  fullWidth
                  multiline
                  rows={2}
                  onChange={e => {
                    field.onChange(e);
                    handleFieldChange("otherFees");
                  }}
                  sx={getFieldStyle("otherFees", "text")}
                />
              )}
            />
          </Grid>

          {/* Management Fees Section */}
          {marketEnumId === 1 && ( // Hedge Fund
            <>
              <Grid item xs={12}>
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  gutterBottom
                  sx={{ mt: 2 }}
                >
                  Management Fees
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>

              <Grid item xs={12} md={3}>
                <Controller
                  name={`shareClasses.${index}.investmentFees.mgmtFee`}
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Management Fee (%)"
                      type="number"
                      fullWidth
                      inputProps={{ step: 0.01 }}
                      onChange={e => {
                        field.onChange(e);
                        handleFieldChange("investmentFees.mgmtFee");
                      }}
                      sx={getFieldStyle("investmentFees.mgmtFee", "text")}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <Controller
                  name={`shareClasses.${index}.investmentFees.mgmtFeeCalcEnumId`}
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <InputLabel>Management Fee Calc</InputLabel>
                      <Select
                        {...field}
                        label="Management Fee Calc"
                        onChange={e => {
                          field.onChange(e);
                          handleFieldChange("investmentFees.mgmtFeeCalcEnumId");
                        }}
                        sx={getFieldStyle(
                          "investmentFees.mgmtFeeCalcEnumId",
                          "select"
                        )}
                      >
                        <MenuItem value="">None</MenuItem>
                        {feeCalcOptions.map(opt => (
                          <MenuItem key={opt.id} value={opt.id}>
                            {opt.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <Controller
                  name={`shareClasses.${index}.investmentFees.mgmtFeeFreqEnumId`}
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <InputLabel>Management Fee Frequency</InputLabel>
                      <Select
                        {...field}
                        label="Management Fee Frequency"
                        onChange={e => {
                          field.onChange(e);
                          handleFieldChange("investmentFees.mgmtFeeFreqEnumId");
                        }}
                        sx={getFieldStyle(
                          "investmentFees.mgmtFeeFreqEnumId",
                          "select"
                        )}
                      >
                        <MenuItem value="">None</MenuItem>
                        {feeFreqOptions.map(opt => (
                          <MenuItem key={opt.id} value={opt.id}>
                            {opt.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <Controller
                  name={`shareClasses.${index}.investmentFees.incentiveFee`}
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Incentive Fee (%)"
                      type="number"
                      fullWidth
                      inputProps={{ step: 0.01 }}
                      onChange={e => {
                        field.onChange(e);
                        handleFieldChange("investmentFees.incentiveFee");
                      }}
                      sx={getFieldStyle("investmentFees.incentiveFee", "text")}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <Controller
                  name={`shareClasses.${index}.investmentFees.incentiveFeeCalcEnumId`}
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <InputLabel>Incentive Fee Calc</InputLabel>
                      <Select
                        {...field}
                        label="Incentive Fee Calc"
                        onChange={e => {
                          field.onChange(e);
                          handleFieldChange(
                            "investmentFees.incentiveFeeCalcEnumId"
                          );
                        }}
                        sx={getFieldStyle(
                          "investmentFees.incentiveFeeCalcEnumId",
                          "select"
                        )}
                      >
                        <MenuItem value="">None</MenuItem>
                        {feeCalcOptions.map(opt => (
                          <MenuItem key={opt.id} value={opt.id}>
                            {opt.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <Controller
                  name={`shareClasses.${index}.investmentFees.incentiveFeeFreqEnumId`}
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <InputLabel>Incentive Fee Frequency</InputLabel>
                      <Select
                        {...field}
                        label="Incentive Fee Frequency"
                        onChange={e => {
                          field.onChange(e);
                          handleFieldChange(
                            "investmentFees.incentiveFeeFreqEnumId"
                          );
                        }}
                        sx={getFieldStyle(
                          "investmentFees.incentiveFeeFreqEnumId",
                          "select"
                        )}
                      >
                        <MenuItem value="">None</MenuItem>
                        {feeFreqOptions.map(opt => (
                          <MenuItem key={opt.id} value={opt.id}>
                            {opt.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <Controller
                  name={`shareClasses.${index}.investmentFees.hurdle`}
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          {...field}
                          checked={field.value || false}
                          onChange={e => {
                            field.onChange(e.target.checked);
                            handleFieldChange("investmentFees.hurdle");
                          }}
                          sx={getFieldStyle(
                            "investmentFees.hurdle",
                            "checkbox"
                          )}
                        />
                      }
                      label="Hurdle"
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <Controller
                  name={`shareClasses.${index}.investmentFees.otherFee`}
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Other Fee (%)"
                      type="number"
                      fullWidth
                      inputProps={{ step: 0.01 }}
                      onChange={e => {
                        field.onChange(e);
                        handleFieldChange("investmentFees.otherFee");
                      }}
                      sx={getFieldStyle("investmentFees.otherFee", "text")}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <Controller
                  name={`shareClasses.${index}.investmentFees.otherFeeCalcEnumId`}
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <InputLabel>Other Fee Calc</InputLabel>
                      <Select
                        {...field}
                        label="Other Fee Calc"
                        onChange={e => {
                          field.onChange(e);
                          handleFieldChange(
                            "investmentFees.otherFeeCalcEnumId"
                          );
                        }}
                        sx={getFieldStyle(
                          "investmentFees.otherFeeCalcEnumId",
                          "select"
                        )}
                      >
                        <MenuItem value="">None</MenuItem>
                        {feeCalcOptions.map(opt => (
                          <MenuItem key={opt.id} value={opt.id}>
                            {opt.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <Controller
                  name={`shareClasses.${index}.investmentFees.otherFeeFreqEnumId`}
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <InputLabel>Other Fee Frequency</InputLabel>
                      <Select
                        {...field}
                        label="Other Fee Frequency"
                        onChange={e => {
                          field.onChange(e);
                          handleFieldChange(
                            "investmentFees.otherFeeFreqEnumId"
                          );
                        }}
                        sx={getFieldStyle(
                          "investmentFees.otherFeeFreqEnumId",
                          "select"
                        )}
                      >
                        <MenuItem value="">None</MenuItem>
                        {feeFreqOptions.map(opt => (
                          <MenuItem key={opt.id} value={opt.id}>
                            {opt.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <Controller
                  name={`shareClasses.${index}.investmentFees.highWaterMark`}
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          {...field}
                          checked={field.value || false}
                          onChange={e => {
                            field.onChange(e.target.checked);
                            handleFieldChange("investmentFees.highWaterMark");
                          }}
                          sx={getFieldStyle(
                            "investmentFees.highWaterMark",
                            "checkbox"
                          )}
                        />
                      }
                      label="High Water Mark?"
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <Controller
                  name={`shareClasses.${index}.investmentFees.annualReset`}
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          {...field}
                          checked={field.value || false}
                          onChange={e => {
                            field.onChange(e.target.checked);
                            handleFieldChange("investmentFees.annualReset");
                          }}
                          sx={getFieldStyle(
                            "investmentFees.annualReset",
                            "checkbox"
                          )}
                        />
                      }
                      label="Annual Reset?"
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Controller
                  name={`shareClasses.${index}.investmentFees.hurdleBenchmark`}
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Hurdle Benchmark"
                      fullWidth
                      helperText="(Example: LIBOR + 100bp)"
                      onChange={e => {
                        field.onChange(e);
                        handleFieldChange("investmentFees.hurdleBenchmark");
                      }}
                      sx={getFieldStyle(
                        "investmentFees.hurdleBenchmark",
                        "text"
                      )}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <Controller
                  name={`shareClasses.${index}.investmentFees.standardShareClassRates`}
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          {...field}
                          checked={field.value || false}
                          onChange={e => {
                            field.onChange(e.target.checked);
                            handleFieldChange(
                              "investmentFees.standardShareClassRates"
                            );
                          }}
                          sx={getFieldStyle(
                            "investmentFees.standardShareClassRates",
                            "checkbox"
                          )}
                        />
                      }
                      label="Standard Share Class Rates"
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <Controller
                  name={`shareClasses.${index}.investmentFees.feeStructureChanged`}
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          {...field}
                          checked={field.value || false}
                          onChange={e => {
                            field.onChange(e.target.checked);
                            handleFieldChange(
                              "investmentFees.feeStructureChanged"
                            );
                          }}
                          sx={getFieldStyle(
                            "investmentFees.feeStructureChanged",
                            "checkbox"
                          )}
                        />
                      }
                      label="Fee Structure Changed"
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Controller
                  name={`shareClasses.${index}.investmentFees.feeStructureChangedNotes`}
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Describe the change in Fee Structure"
                      fullWidth
                      multiline
                      rows={3}
                      disabled={
                        !watch(
                          `shareClasses.${index}.investmentFees.feeStructureChanged`
                        )
                      }
                      onChange={e => {
                        field.onChange(e);
                        handleFieldChange(
                          "investmentFees.feeStructureChangedNotes"
                        );
                      }}
                      sx={getFieldStyle(
                        "investmentFees.feeStructureChangedNotes",
                        "text"
                      )}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <Controller
                  name={`shareClasses.${index}.investmentFees.abnormalFees`}
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          {...field}
                          checked={field.value || false}
                          onChange={e => {
                            field.onChange(e.target.checked);
                            handleFieldChange("investmentFees.abnormalFees");
                          }}
                          sx={getFieldStyle(
                            "investmentFees.abnormalFees",
                            "checkbox"
                          )}
                        />
                      }
                      label="Abnormal Fees"
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={9}>
                <Controller
                  name={`shareClasses.${index}.investmentFees.abnormalFeesNotes`}
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Describe the abnormal fees"
                      fullWidth
                      multiline
                      rows={3}
                      disabled={
                        !watch(
                          `shareClasses.${index}.investmentFees.abnormalFees`
                        )
                      }
                      onChange={e => {
                        field.onChange(e);
                        handleFieldChange("investmentFees.abnormalFeesNotes");
                      }}
                      sx={getFieldStyle(
                        "investmentFees.abnormalFeesNotes",
                        "text"
                      )}
                    />
                  )}
                />
              </Grid>

              {/* Liquidity Section */}
              <Grid item xs={12}>
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  gutterBottom
                  sx={{ mt: 2 }}
                >
                  Liquidity Terms
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>

              {/* Subscription */}
              <Grid item xs={12} md={3}>
                <Controller
                  name={`shareClasses.${index}.liquidity.subscriptionValue`}
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Subscription Frequency"
                      type="number"
                      fullWidth
                      onChange={e => {
                        field.onChange(e);
                        handleFieldChange("liquidity.subscriptionValue");
                      }}
                      sx={getFieldStyle("liquidity.subscriptionValue", "text")}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <Controller
                  name={`shareClasses.${index}.liquidity.subscriptionUnitId`}
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <InputLabel>Unit</InputLabel>
                      <Select
                        {...field}
                        label="Unit"
                        onChange={e => {
                          field.onChange(e);
                          handleFieldChange("liquidity.subscriptionUnitId");
                        }}
                        sx={getFieldStyle(
                          "liquidity.subscriptionUnitId",
                          "select"
                        )}
                      >
                        <MenuItem value="">None</MenuItem>
                        {calendarUnitOptions.map(opt => (
                          <MenuItem key={opt.id} value={opt.id}>
                            {opt.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>

              {/* Lockup */}
              <Grid item xs={12} md={3}>
                <Controller
                  name={`shareClasses.${index}.liquidity.hasLockup`}
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <InputLabel>Lockup?</InputLabel>
                      <Select
                        {...field}
                        label="Lockup?"
                        value={
                          field.value === null || field.value === undefined
                            ? ""
                            : field.value === true
                            ? "true"
                            : "false"
                        }
                        onChange={e => {
                          const val =
                            e.target.value === ""
                              ? null
                              : e.target.value === "true";
                          field.onChange(val);
                          handleFieldChange("liquidity.hasLockup");
                        }}
                        sx={getFieldStyle("liquidity.hasLockup", "select")}
                      >
                        <MenuItem value="">None</MenuItem>
                        <MenuItem value="true">Yes</MenuItem>
                        <MenuItem value="false">No</MenuItem>
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <Controller
                  name={`shareClasses.${index}.liquidity.lockupValue`}
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Lockup Period"
                      type="number"
                      fullWidth
                      disabled={
                        watch(`shareClasses.${index}.liquidity.hasLockup`) !==
                        true
                      }
                      onChange={e => {
                        field.onChange(e);
                        handleFieldChange("liquidity.lockupValue");
                      }}
                      sx={getFieldStyle("liquidity.lockupValue", "text")}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <Controller
                  name={`shareClasses.${index}.liquidity.lockupEndValueId`}
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <InputLabel>First Redemption on</InputLabel>
                      <Select
                        {...field}
                        label="First Redemption on"
                        onChange={e => {
                          field.onChange(e);
                          handleFieldChange("liquidity.lockupEndValueId");
                        }}
                        sx={getFieldStyle(
                          "liquidity.lockupEndValueId",
                          "select"
                        )}
                      >
                        <MenuItem value="">None</MenuItem>
                        {lockupEndValueOptions.map(opt => (
                          <MenuItem key={opt.id} value={opt.id}>
                            {opt.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <Controller
                  name={`shareClasses.${index}.liquidity.lockupEndBizDays`}
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          {...field}
                          checked={field.value || false}
                          onChange={e => {
                            field.onChange(e.target.checked);
                            handleFieldChange("liquidity.lockupEndBizDays");
                          }}
                          sx={getFieldStyle(
                            "liquidity.lockupEndBizDays",
                            "checkbox"
                          )}
                        />
                      }
                      label="Last Business Day"
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <Controller
                  name={`shareClasses.${index}.liquidity.secondLockupEndValueId`}
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <InputLabel>Subsequent Redemption on</InputLabel>
                      <Select
                        {...field}
                        label="Subsequent Redemption on"
                        onChange={e => {
                          field.onChange(e);
                          handleFieldChange("liquidity.secondLockupEndValueId");
                        }}
                        sx={getFieldStyle(
                          "liquidity.secondLockupEndValueId",
                          "select"
                        )}
                      >
                        <MenuItem value="">None</MenuItem>
                        {lockupEndValueOptions.map(opt => (
                          <MenuItem key={opt.id} value={opt.id}>
                            {opt.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <Controller
                  name={`shareClasses.${index}.liquidity.secondLockupEndBizDays`}
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          {...field}
                          checked={field.value || false}
                          onChange={e => {
                            field.onChange(e.target.checked);
                            handleFieldChange(
                              "liquidity.secondLockupEndBizDays"
                            );
                          }}
                          sx={getFieldStyle(
                            "liquidity.secondLockupEndBizDays",
                            "checkbox"
                          )}
                        />
                      }
                      label="Last Business Day"
                    />
                  )}
                />
              </Grid>

              {/* Liquidity */}
              <Grid item xs={12} md={3}>
                <Controller
                  name={`shareClasses.${index}.liquidity.liquidityValue`}
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Liquidity Period"
                      type="number"
                      fullWidth
                      onChange={e => {
                        field.onChange(e);
                        handleFieldChange("liquidity.liquidityValue");
                      }}
                      sx={getFieldStyle("liquidity.liquidityValue", "text")}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={2}>
                <Controller
                  name={`shareClasses.${index}.liquidity.liquidityUnitId`}
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <InputLabel>Unit</InputLabel>
                      <Select
                        {...field}
                        label="Unit"
                        onChange={e => {
                          field.onChange(e);
                          handleFieldChange("liquidity.liquidityUnitId");
                        }}
                        sx={getFieldStyle(
                          "liquidity.liquidityUnitId",
                          "select"
                        )}
                      >
                        <MenuItem value="">None</MenuItem>
                        {calendarUnitOptions.map(opt => (
                          <MenuItem key={opt.id} value={opt.id}>
                            {opt.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <Controller
                  name={`shareClasses.${index}.liquidity.cashAvailableDays`}
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <InputLabel>Cash Availability</InputLabel>
                      <Select
                        {...field}
                        label="Cash Availability"
                        onChange={e => {
                          field.onChange(e);
                          handleFieldChange("liquidity.cashAvailableDays");
                        }}
                        sx={getFieldStyle(
                          "liquidity.cashAvailableDays",
                          "select"
                        )}
                      >
                        <MenuItem value="">None</MenuItem>
                        {CASH_AVAILABLE_DAYS_OPTIONS.map(opt => (
                          <MenuItem key={opt.id} value={opt.id}>
                            {opt.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>

              <Grid item xs={12} md={2}>
                <Controller
                  name={`shareClasses.${index}.liquidity.noticeValue`}
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Notice Period"
                      type="number"
                      fullWidth
                      onChange={e => {
                        field.onChange(e);
                        handleFieldChange("liquidity.noticeValue");
                      }}
                      sx={getFieldStyle("liquidity.noticeValue", "text")}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={2}>
                <Controller
                  name={`shareClasses.${index}.liquidity.noticeBizDays`}
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <InputLabel>Business Days</InputLabel>
                      <Select
                        {...field}
                        label="Business Days"
                        value={
                          field.value === null || field.value === undefined
                            ? ""
                            : field.value === true
                            ? "true"
                            : "false"
                        }
                        onChange={e => {
                          const val =
                            e.target.value === ""
                              ? null
                              : e.target.value === "true";
                          field.onChange(val);
                          handleFieldChange("liquidity.noticeBizDays");
                        }}
                        sx={getFieldStyle("liquidity.noticeBizDays", "select")}
                      >
                        <MenuItem value="">None</MenuItem>
                        <MenuItem value="true">Business Days</MenuItem>
                        <MenuItem value="false">Calendar Days</MenuItem>
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>

              {/* Gate */}
              <Grid item xs={12} md={2}>
                <Controller
                  name={`shareClasses.${index}.liquidity.hasGate`}
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <InputLabel>Gate?</InputLabel>
                      <Select
                        {...field}
                        label="Gate?"
                        value={
                          field.value === null || field.value === undefined
                            ? ""
                            : field.value === true
                            ? "true"
                            : "false"
                        }
                        onChange={e => {
                          const val =
                            e.target.value === ""
                              ? null
                              : e.target.value === "true";
                          field.onChange(val);
                          handleFieldChange("liquidity.hasGate");
                        }}
                        sx={getFieldStyle("liquidity.hasGate", "select")}
                      >
                        <MenuItem value="">None</MenuItem>
                        <MenuItem value="true">Yes</MenuItem>
                        <MenuItem value="false">No</MenuItem>
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>

              <Grid item xs={12} md={2}>
                <Controller
                  name={`shareClasses.${index}.liquidity.gatePercentCapital`}
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Investor Level Gate (%)"
                      type="number"
                      fullWidth
                      disabled={
                        watch(`shareClasses.${index}.liquidity.hasGate`) !==
                        true
                      }
                      inputProps={{ step: 0.01 }}
                      onChange={e => {
                        field.onChange(e);
                        handleFieldChange("liquidity.gatePercentCapital");
                      }}
                      sx={getFieldStyle("liquidity.gatePercentCapital", "text")}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={2}>
                <Controller
                  name={`shareClasses.${index}.liquidity.gatePercentNav`}
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Fund Level Gate (%)"
                      type="number"
                      fullWidth
                      disabled={
                        watch(`shareClasses.${index}.liquidity.hasGate`) !==
                        true
                      }
                      inputProps={{ step: 0.01 }}
                      onChange={e => {
                        field.onChange(e);
                        handleFieldChange("liquidity.gatePercentNav");
                      }}
                      sx={getFieldStyle("liquidity.gatePercentNav", "text")}
                    />
                  )}
                />
              </Grid>

              {/* Holdback */}
              <Grid item xs={12} md={2}>
                <Controller
                  name={`shareClasses.${index}.liquidity.hasHoldback`}
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <InputLabel>Holdback?</InputLabel>
                      <Select
                        {...field}
                        label="Holdback?"
                        value={
                          field.value === null || field.value === undefined
                            ? ""
                            : field.value === true
                            ? "true"
                            : "false"
                        }
                        onChange={e => {
                          const val =
                            e.target.value === ""
                              ? null
                              : e.target.value === "true";
                          field.onChange(val);
                          handleFieldChange("liquidity.hasHoldback");
                        }}
                        sx={getFieldStyle("liquidity.hasHoldback", "select")}
                      >
                        <MenuItem value="">None</MenuItem>
                        <MenuItem value="true">Yes</MenuItem>
                        <MenuItem value="false">No</MenuItem>
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>

              <Grid item xs={12} md={2}>
                <Controller
                  name={`shareClasses.${index}.liquidity.holdbackPercent`}
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Holdback (%)"
                      type="number"
                      fullWidth
                      disabled={
                        watch(`shareClasses.${index}.liquidity.hasHoldback`) !==
                        true
                      }
                      inputProps={{ step: 0.01 }}
                      onChange={e => {
                        field.onChange(e);
                        handleFieldChange("liquidity.holdbackPercent");
                      }}
                      sx={getFieldStyle("liquidity.holdbackPercent", "text")}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={2}>
                <Controller
                  name={`shareClasses.${index}.liquidity.workoutId`}
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <InputLabel>Workout</InputLabel>
                      <Select
                        {...field}
                        label="Workout"
                        onChange={e => {
                          field.onChange(e);
                          handleFieldChange("liquidity.workoutId");
                        }}
                        sx={getFieldStyle("liquidity.workoutId", "select")}
                      >
                        <MenuItem value="">None</MenuItem>
                        {workoutOptions.map(opt => (
                          <MenuItem key={opt.id} value={opt.id}>
                            {opt.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>

              {/* Exceptions and Notes */}
              <Grid item xs={12} md={6}>
                <Controller
                  name={`shareClasses.${index}.liquidity.exceptions`}
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Can the liquidity be modeled using the information above? If not, note the exceptions below."
                      multiline
                      rows={5}
                      fullWidth
                      onChange={e => {
                        field.onChange(e);
                        handleFieldChange("liquidity.exceptions");
                      }}
                      sx={getFieldStyle("liquidity.exceptions", "text")}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Controller
                  name={`shareClasses.${index}.liquidity.modelingNotes`}
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Additional notes that do not affect liquidity modeling."
                      multiline
                      rows={5}
                      fullWidth
                      onChange={e => {
                        field.onChange(e);
                        handleFieldChange("liquidity.modelingNotes");
                      }}
                      sx={getFieldStyle("liquidity.modelingNotes", "text")}
                    />
                  )}
                />
              </Grid>
            </>
          )}

          {/* PE-specific Fees */}
          {marketEnumId === 2 && ( // Private Equity
            <>
              <Grid item xs={12}>
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  gutterBottom
                  sx={{ mt: 2 }}
                >
                  Management Fees (PE)
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>

              <Grid item xs={12} md={4}>
                <Controller
                  name={`shareClasses.${index}.investmentFees.mgmtFee`}
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Management Fee (%)"
                      type="number"
                      fullWidth
                      inputProps={{ step: 0.01 }}
                      onChange={e => {
                        field.onChange(e);
                        handleFieldChange("investmentFees.mgmtFee");
                      }}
                      sx={getFieldStyle("investmentFees.mgmtFee", "text")}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <Controller
                  name={`shareClasses.${index}.investmentFees.peMgmtFeeCalcEnumId`}
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <InputLabel>PE Management Fee Calc</InputLabel>
                      <Select
                        {...field}
                        label="PE Management Fee Calc"
                        onChange={e => {
                          field.onChange(e);
                          handleFieldChange(
                            "investmentFees.peMgmtFeeCalcEnumId"
                          );
                        }}
                        sx={getFieldStyle(
                          "investmentFees.peMgmtFeeCalcEnumId",
                          "select"
                        )}
                      >
                        <MenuItem value="">None</MenuItem>
                        {peMgmtFeeCalcOptions.map(opt => (
                          <MenuItem key={opt.id} value={opt.id}>
                            {opt.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <Controller
                  name={`shareClasses.${index}.investmentFees.mgmtFeeFreqEnumId`}
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <InputLabel>Fee Frequency</InputLabel>
                      <Select
                        {...field}
                        label="Fee Frequency"
                        onChange={e => {
                          field.onChange(e);
                          handleFieldChange("investmentFees.mgmtFeeFreqEnumId");
                        }}
                        sx={getFieldStyle(
                          "investmentFees.mgmtFeeFreqEnumId",
                          "select"
                        )}
                      >
                        <MenuItem value="">None</MenuItem>
                        {feeFreqOptions.map(opt => (
                          <MenuItem key={opt.id} value={opt.id}>
                            {opt.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <Controller
                  name={`shareClasses.${index}.investmentFees.incentiveFee`}
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Incentive Fee (%)"
                      type="number"
                      fullWidth
                      inputProps={{ step: 0.01 }}
                      onChange={e => {
                        field.onChange(e);
                        handleFieldChange("investmentFees.incentiveFee");
                      }}
                      sx={getFieldStyle("investmentFees.incentiveFee", "text")}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <Controller
                  name={`shareClasses.${index}.investmentFees.peIncentiveFeeCalcEnumId`}
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <InputLabel>Incentive Fee Base</InputLabel>
                      <Select
                        {...field}
                        label="Incentive Fee Base"
                        onChange={e => {
                          field.onChange(e);
                          handleFieldChange(
                            "investmentFees.peIncentiveFeeCalcEnumId"
                          );
                        }}
                        sx={getFieldStyle(
                          "investmentFees.peIncentiveFeeCalcEnumId",
                          "select"
                        )}
                      >
                        <MenuItem value="">None</MenuItem>
                        {peInctFeeCalcOptions.map(opt => (
                          <MenuItem key={opt.id} value={opt.id}>
                            {opt.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <Controller
                  name={`shareClasses.${index}.investmentFees.incentiveFeeFreqEnumId`}
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <InputLabel>Incentive Fee Frequency</InputLabel>
                      <Select
                        {...field}
                        label="Incentive Fee Frequency"
                        onChange={e => {
                          field.onChange(e);
                          handleFieldChange(
                            "investmentFees.incentiveFeeFreqEnumId"
                          );
                        }}
                        sx={getFieldStyle(
                          "investmentFees.incentiveFeeFreqEnumId",
                          "select"
                        )}
                      >
                        <MenuItem value="">None</MenuItem>
                        {feeFreqOptions.map(opt => (
                          <MenuItem key={opt.id} value={opt.id}>
                            {opt.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <Controller
                  name={`shareClasses.${index}.investmentFees.peMgmtFeeDuringCommitment`}
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Mgmt Fee During Commitment (%)"
                      type="number"
                      fullWidth
                      inputProps={{ step: 0.01 }}
                      onChange={e => {
                        field.onChange(e);
                        handleFieldChange(
                          "investmentFees.peMgmtFeeDuringCommitment"
                        );
                      }}
                      sx={getFieldStyle(
                        "investmentFees.peMgmtFeeDuringCommitment",
                        "text"
                      )}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <Controller
                  name={`shareClasses.${index}.investmentFees.peMgmtFeeDuringCommitmentCalcEnumId`}
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <InputLabel>Fee Base</InputLabel>
                      <Select
                        {...field}
                        label="Fee Base"
                        onChange={e => {
                          field.onChange(e);
                          handleFieldChange(
                            "investmentFees.peMgmtFeeDuringCommitmentCalcEnumId"
                          );
                        }}
                        sx={getFieldStyle(
                          "investmentFees.peMgmtFeeDuringCommitmentCalcEnumId",
                          "select"
                        )}
                      >
                        <MenuItem value="">None</MenuItem>
                        {peMgmtFeeCalcOptions.map(opt => (
                          <MenuItem key={opt.id} value={opt.id}>
                            {opt.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <Controller
                  name={`shareClasses.${index}.investmentFees.peMgmtFeeDuringHarvest`}
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Mgmt Fee During Harvest (%)"
                      type="number"
                      fullWidth
                      inputProps={{ step: 0.01 }}
                      onChange={e => {
                        field.onChange(e);
                        handleFieldChange(
                          "investmentFees.peMgmtFeeDuringHarvest"
                        );
                      }}
                      sx={getFieldStyle(
                        "investmentFees.peMgmtFeeDuringHarvest",
                        "text"
                      )}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <Controller
                  name={`shareClasses.${index}.investmentFees.peMgmtFeeDuringHarvestCalcEnumId`}
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <InputLabel>Fee Base</InputLabel>
                      <Select
                        {...field}
                        label="Fee Base"
                        onChange={e => {
                          field.onChange(e);
                          handleFieldChange(
                            "investmentFees.peMgmtFeeDuringHarvestCalcEnumId"
                          );
                        }}
                        sx={getFieldStyle(
                          "investmentFees.peMgmtFeeDuringHarvestCalcEnumId",
                          "select"
                        )}
                      >
                        <MenuItem value="">None</MenuItem>
                        {peMgmtFeeCalcOptions.map(opt => (
                          <MenuItem key={opt.id} value={opt.id}>
                            {opt.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <Controller
                  name={`shareClasses.${index}.investmentFees.hasMgmtFeeStepDown`}
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          {...field}
                          checked={field.value || false}
                          onChange={e => {
                            field.onChange(e.target.checked);
                            handleFieldChange(
                              "investmentFees.hasMgmtFeeStepDown"
                            );
                          }}
                          sx={getFieldStyle(
                            "investmentFees.hasMgmtFeeStepDown",
                            "checkbox"
                          )}
                        />
                      }
                      label="Has Mgmt Fee Step Down?"
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <Controller
                  name={`shareClasses.${index}.investmentFees.peMgmtFeeStepDownPercent`}
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Step Down Percentage (%)"
                      type="number"
                      fullWidth
                      inputProps={{ step: 0.01 }}
                      disabled={
                        !watch(
                          `shareClasses.${index}.investmentFees.hasMgmtFeeStepDown`
                        )
                      }
                      onChange={e => {
                        field.onChange(e);
                        handleFieldChange(
                          "investmentFees.peMgmtFeeStepDownPercent"
                        );
                      }}
                      sx={getFieldStyle(
                        "investmentFees.peMgmtFeeStepDownPercent",
                        "text"
                      )}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <Controller
                  name={`shareClasses.${index}.investmentFees.peMgmtFeeStepDownCalcEnumId`}
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <InputLabel>Step Down Fee Base</InputLabel>
                      <Select
                        {...field}
                        label="Step Down Fee Base"
                        disabled={
                          !watch(
                            `shareClasses.${index}.investmentFees.hasMgmtFeeStepDown`
                          )
                        }
                        onChange={e => {
                          field.onChange(e);
                          handleFieldChange(
                            "investmentFees.peMgmtFeeStepDownCalcEnumId"
                          );
                        }}
                        sx={getFieldStyle(
                          "investmentFees.peMgmtFeeStepDownCalcEnumId",
                          "select"
                        )}
                      >
                        <MenuItem value="">None</MenuItem>
                        {peMgmtFeeCalcOptions.map(opt => (
                          <MenuItem key={opt.id} value={opt.id}>
                            {opt.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <Controller
                  name={`shareClasses.${index}.peLiquidity.optionOfExtensionId`}
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <InputLabel>Right Of Extension</InputLabel>
                      <Select
                        {...field}
                        label="Right Of Extension"
                        onChange={e => {
                          field.onChange(e);
                          handleFieldChange("peLiquidity.optionOfExtensionId");
                        }}
                        sx={getFieldStyle(
                          "peLiquidity.optionOfExtensionId",
                          "select"
                        )}
                      >
                        <MenuItem value="">None</MenuItem>
                        {rightOfExtensionOptions.map(opt => (
                          <MenuItem key={opt.id} value={opt.id}>
                            {opt.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <Controller
                  name={`shareClasses.${index}.peLiquidity.preferredReturn`}
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Preferred Return (%)"
                      type="number"
                      fullWidth
                      inputProps={{ step: 0.01 }}
                      onChange={e => {
                        field.onChange(e);
                        handleFieldChange("peLiquidity.preferredReturn");
                      }}
                      sx={getFieldStyle("peLiquidity.preferredReturn", "text")}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <Controller
                  name={`shareClasses.${index}.peLiquidity.isCompounded`}
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          {...field}
                          checked={field.value || false}
                          onChange={e => {
                            field.onChange(e.target.checked);
                            handleFieldChange("peLiquidity.isCompounded");
                          }}
                          sx={getFieldStyle(
                            "peLiquidity.isCompounded",
                            "checkbox"
                          )}
                        />
                      }
                      label="Is Compounded"
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <Controller
                  name={`shareClasses.${index}.peLiquidity.compoundingPeriodId`}
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <InputLabel>Compounding Period</InputLabel>
                      <Select
                        {...field}
                        label="Compounding Period"
                        disabled={
                          !watch(
                            `shareClasses.${index}.peLiquidity.isCompounded`
                          )
                        }
                        onChange={e => {
                          field.onChange(e);
                          handleFieldChange("peLiquidity.compoundingPeriodId");
                        }}
                        sx={getFieldStyle(
                          "peLiquidity.compoundingPeriodId",
                          "select"
                        )}
                      >
                        <MenuItem value="">None</MenuItem>
                        {compoundingPeriodOptions.map(opt => (
                          <MenuItem key={opt.id} value={opt.id}>
                            {opt.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <Controller
                  name={`shareClasses.${index}.peLiquidity.hasCatchupProvision`}
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          {...field}
                          checked={field.value || false}
                          onChange={e => {
                            field.onChange(e.target.checked);
                            handleFieldChange(
                              "peLiquidity.hasCatchupProvision"
                            );
                          }}
                          sx={getFieldStyle(
                            "peLiquidity.hasCatchupProvision",
                            "checkbox"
                          )}
                        />
                      }
                      label="Has Catch-up Provision?"
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <Controller
                  name={`shareClasses.${index}.peLiquidity.catchupPercent`}
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Catch-up Percentage (%)"
                      type="number"
                      fullWidth
                      inputProps={{ step: 0.01 }}
                      disabled={
                        !watch(
                          `shareClasses.${index}.peLiquidity.hasCatchupProvision`
                        )
                      }
                      onChange={e => {
                        field.onChange(e);
                        handleFieldChange("peLiquidity.catchupPercent");
                      }}
                      sx={getFieldStyle("peLiquidity.catchupPercent", "text")}
                    />
                  )}
                />
              </Grid>
            </>
          )}
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};

export default ShareClassRowForm;
